'use client'

import { memo } from 'react'

import type { CustomActionConfig } from '../types'
import { CustomActionButton } from './CustomActionButton'
import { ExternalIcon } from '@components/Layout/NavLink'
import { DropdownMenuItem } from '@ui/DropDownMenu'

interface CustomActionRendererProps {
  customActions: Record<string, CustomActionConfig>
  accountId?: number | null
  /** Whether to render as dropdown items instead of buttons */
  asDropdownItems?: boolean
  /** Callback when action is triggered */
  onActionClick?: (key: string) => void
}

export const CustomActionRenderer = memo<CustomActionRendererProps>(
  ({ customActions, accountId, asDropdownItems = false, onActionClick }) => {
    return (
      <>
        {Object.entries(customActions).map(([actionName, config]) => {
          const { condition, button = {} } = config
          const label = button.label ?? actionName.replaceAll('_', ' ')

          // Check condition if provided
          if (condition && !condition(accountId)) {
            return null
          }

          if (asDropdownItems) {
            // Render as dropdown menu item for hidden actions
            return (
              <DropdownMenuItem
                key={actionName}
                textValue={label.replaceAll(' ', '_')}
                onSelect={() => onActionClick?.(actionName)}
              >
                {label}
                {button.external && <ExternalIcon />}
              </DropdownMenuItem>
            )
          }

          // Render as visible button
          return (
            <CustomActionButton
              key={actionName}
              label={label}
              icon={button.icon}
              variant={button.variant}
              size={button.size}
              external={button.external}
              onClick={() => onActionClick?.(actionName)}
            />
          )
        })}
      </>
    )
  },
)

CustomActionRenderer.displayName = 'CustomActionRenderer'
